<template>
  <Combobox
    v-slot="{ open }"
    class="relative w-full"
    as="div"
  >
    <div class="flex items-center rounded-md border border-gray-300 p-2 px-4 font-light text-gray-700 transition-all">
      <MagnifyingGlassIcon class="mr-3 size-5 shrink-0 text-gray-500" />
      <ComboboxInput
        :displayValue="() => searchText"
        placeholder="Buscar productos o marcas..."
        class="w-full bg-transparent text-sm font-light placeholder:font-light placeholder:text-gray-400 focus:outline-none md:text-base"
        @input="handleInputEvent"
        @change="handleInputChange"
        @keydown.enter="handleEnterKey"
      />
      <XMarkIcon
        v-if="searchText"
        class="size-5 shrink-0 cursor-pointer text-gray-500 hover:text-gray-700"
        @click="clearSearch"
      />
    </div>

    <transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0 -translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-2"
    >
      <ComboboxOptions
        v-if="open && searchText"
        class="absolute inset-x-0 z-50 mt-1 max-h-96 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg"
      >
        <div
          v-if="isSearchLoading"
          class="p-4 text-center"
        >
          <span class="text-gray-500">Buscando...</span>
        </div>

        <template v-if="(products.length > 0 || makers.length > 0) && !isSearchLoading">
          <div class="p-2">
            <p class="mb-1 text-xs font-semibold uppercase text-gray-500">Productos</p>
            <ComboboxOption
              v-for="product in products.slice(0, 5)"
              :key="`search-product-${product.id}`"
              v-slot="{ active }"
              :value="product"
              as="ul"
            >
              <li
                :class="{ 'bg-gray-100': active }"
                class="rounded p-2 transition-colors hover:bg-gray-50"
                @click="navigateToProduct(product)"
              >
                <div class="flex items-center">
                  <div class="relative mr-2 size-10 rounded bg-gray-200">
                    <div
                      v-if="product.images?.[0]?.derivatives?.jpgSm?.url"
                      class="size-full rounded bg-cover bg-center"
                      :style="{
                        backgroundImage: `url(${product.images[0].derivatives.jpgSm.url})`,
                        backgroundSize: 'cover',
                      }"
                      :class="{ 'opacity-50': isProductOutOfStock(product) }"
                    />
                    <div
                      v-else
                      class="flex size-full items-center justify-center rounded"
                      :class="{ 'opacity-50': isProductOutOfStock(product) }"
                    >
                      <CubeIcon class="size-4 text-gray-400" />
                    </div>
                  </div>
                  <div class="flex-1">
                    <p class="text-sm font-medium">{{ product.name }}</p>
                    <p class="text-xs text-gray-500">{{ getMakerName(product) }}</p>
                    <p
                      v-if="isProductOutOfStock(product)"
                      class="text-xs font-light text-red-900"
                    >
                      Agotado
                    </p>
                  </div>
                </div>
              </li>
            </ComboboxOption>
          </div>
          <div class="p-2">
            <p class="mb-1 text-xs font-semibold uppercase text-gray-500">Marcas</p>
            <ComboboxOption
              v-for="maker in makers.slice(0, 5)"
              :key="`search-maker-${maker.id}`"
              v-slot="{ active }"
              :value="maker"
              as="ul"
            >
              <li
                :class="{ 'bg-gray-100': active }"
                class="cursor-pointer rounded p-2 transition-colors hover:bg-gray-50"
                @click="navigateToMaker(maker)"
              >
                <div class="flex items-center">
                  <div
                    v-if="maker.logoImage?.derivatives?.jpgSm?.url"
                    class="mr-2 size-10 shrink-0 rounded-full border border-gray-200 bg-gray-200"
                    :style="{
                      backgroundImage: `url(${maker.logoImage.derivatives.jpgSm.url})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }"
                  />
                  <div
                    v-else
                    class="mr-2 flex size-10 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-gray-100"
                  >
                    <UserIcon class="size-4 text-gray-400" />
                  </div>
                  <div class="flex-1">
                    <div class="text-sm font-medium">{{ maker.name }}</div>
                  </div>
                </div>
              </li>
            </ComboboxOption>
          </div>
        </template>
      </ComboboxOptions>
    </transition>
  </Combobox>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { Combobox, ComboboxInput, ComboboxOptions, ComboboxOption } from '@headlessui/vue';
import { MagnifyingGlassIcon, XMarkIcon, CubeIcon, UserIcon } from '@heroicons/vue/24/outline';
import { debounce } from 'lodash';
import { useQuery } from 'vue-query';
import searchApi from 'api/search';

const searchText = ref('');
const debouncedSearchText = ref('');
const products = ref<SearchProduct[]>([]);
const makers = ref<SearchMaker[]>([]);

const debouncedSearch = debounce((value: string) => {
  debouncedSearchText.value = value;
}, 300);

function handleInputEvent(event: Event) {
  const target = event.target as HTMLInputElement;
  searchText.value = target.value;
  debouncedSearch(searchText.value);
}

function handleInputChange(event: Event) {
  const target = event.target as HTMLInputElement;
  searchText.value = target.value;
  debouncedSearch(searchText.value);
}

const searchEnabled = computed(() => debouncedSearchText.value.length >= 2);

const { isLoading: isSearchLoading } = useQuery(
  ['globalSearch', debouncedSearchText],
  () => {
    if (debouncedSearchText.value.length < 2) {
      return Promise.resolve({ data: { products: [], makers: [] } });
    }
    return searchApi.unified(debouncedSearchText.value);
  },
  {
    enabled: searchEnabled,
    onSuccess: (response) => {
      products.value = response.data.products || [];
      makers.value = response.data.makers || [];
    },
    onError: () => {
      products.value = [];
      makers.value = [];
    },
    staleTime: 30000,
    cacheTime: 60000,
  },
);

watch(debouncedSearchText, (newValue) => {
  if (newValue.length < 2) {
    products.value = [];
    makers.value = [];
  }
});
function getMakerName(product: SearchProduct): string {
  return product.makerName || '';
}

function isProductOutOfStock(product: SearchProduct): boolean {
  return product.status === 'out_of_stock' || (!!product.inventory?.enable && product.inventory?.quantity === 0);
}

function clearSearch() {
  searchText.value = '';
  debouncedSearchText.value = '';
  products.value = [];
  makers.value = [];
}

function navigateToProduct(product: SearchProduct) {
  window.location.href = `/products/${product.id}`;
  clearSearch();
}

function navigateToMaker(maker: SearchMaker) {
  window.location.href = `/makers/${maker.slug}`;
  clearSearch();
}

function handleEnterKey() {
  if (searchText.value.trim()) {
    window.location.href = `/products?search_text=${encodeURIComponent(searchText.value.trim())}`;
    clearSearch();
  }
}
</script>
